import { useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { useTranslation } from '@parentpath/i18n';
import { useSuggestions } from '../hooks/useSuggestions';
import { SuggestNameModal } from '../components/SuggestNameModal';

// Gender icon components
const GenderIcon = ({ gender }: { gender: 'male' | 'female' | 'neutral' }) => {
  const symbols = {
    male: { symbol: '♂', className: 'gender-male' },
    female: { symbol: '♀', className: 'gender-female' },
    neutral: { symbol: '⚥', className: 'gender-neutral' },
  };
  const config = symbols[gender];
  return <span className={cn('text-lg', config.className)}>{config.symbol}</span>;
};

export function MySuggestionsPage() {
  const { t } = useTranslation('names');
  const {
    suggestions,
    isLoading,
    pendingCount,
    approvedCount,
    rejectedCount,
    withdrawSuggestion,
  } = useSuggestions();
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);

  const handleWithdraw = async (id: string) => {
    setWithdrawingId(id);
    await withdrawSuggestion(id);
    setWithdrawingId(null);
  };

  const filteredSuggestions = suggestions.filter((s) => {
    if (filter === 'all') return true;
    if (filter === 'rejected') return s.status === 'rejected' || s.status === 'duplicate';
    return s.status === filter;
  });

  const filterTabs = [
    { value: 'all', label: t('suggestionsPage.all'), count: suggestions.length },
    { value: 'pending', label: t('suggestionsPage.pending'), count: pendingCount },
    { value: 'approved', label: t('suggestionsPage.approved'), count: approvedCount },
    { value: 'rejected', label: t('suggestionsPage.rejected'), count: rejectedCount },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-4">{t('suggestionsPage.title')}</h1>

      {/* Status Cards */}
      <div className="flex gap-3 mb-6">
        <div className="status-card status-card-pending">
          <div className="status-card-number">{pendingCount}</div>
          <div className="status-card-label">{t('suggestionsPage.pending')}</div>
        </div>
        <div className="status-card status-card-approved">
          <div className="status-card-number">{approvedCount}</div>
          <div className="status-card-label">{t('suggestionsPage.approved')}</div>
        </div>
        <div className="status-card status-card-rejected">
          <div className="status-card-number">{rejectedCount}</div>
          <div className="status-card-label">{t('suggestionsPage.rejected')}</div>
        </div>
      </div>

      {/* New Button Row */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowModal(true)}
          className="bg-pp-orange rounded-md border border-white/25 px-3 py-1 flex items-center gap-1 text-white text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          {t('suggestionsPage.new')}
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs mb-4 mt-8">
        {filterTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value as typeof filter)}
            className={cn(
              'filter-tab',
              filter === tab.value && 'active'
            )}
          >
            {tab.label}
            <span>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Suggestions List */}
      <section>
        {filteredSuggestions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-white/60" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {filter === 'all'
                ? t('suggestionsPage.empty')
                : t('suggestionsPage.emptyFiltered', {
                    status:
                      filter === 'pending'
                        ? t('suggestionsPage.emptyPending')
                        : filter === 'approved'
                        ? t('suggestionsPage.emptyApproved')
                        : t('suggestionsPage.emptyRejected'),
                  })}
            </h3>
            {filter === 'all' && (
              <>
                <p className="text-white/70 mb-4">
                  {t('suggestionsPage.emptyHint')}
                </p>
                <Button
                  onClick={() => setShowModal(true)}
                  className="bg-pp-orange hover:bg-pp-orange/90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('suggestionsPage.suggestName')}
                </Button>
              </>
            )}
          </div>
        ) : (
          filteredSuggestions.map((suggestion) => (
            <div key={suggestion.id} className="name-list-card">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-semibold text-lg">{suggestion.name}</span>
                  <GenderIcon gender={suggestion.gender} />
                </div>
              </div>
              <div className="flex items-center gap-4">
                {suggestion.status === 'pending' && (
                  <button
                    onClick={() => handleWithdraw(suggestion.id)}
                    disabled={withdrawingId === suggestion.id}
                    className="list-action-btn delete"
                  >
                    {withdrawingId === suggestion.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Trash2 className="w-5 h-5" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </section>

      {/* Suggest Modal */}
      <SuggestNameModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}
